import { operationIds } from "@gen/app-access-config";
import type { IClientWebhook } from "@gen/app-query";
import {
  CreateClientWebhookBody,
  Query,
  UpdateClientBody,
  UpdateClientWebhookBody,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CopyableTextInput } from "~/components/copyable_text_input";
import { HeadingContainer } from "~/components/heading_container";
import { HideableText } from "~/components/hideable_text";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  SectionSideNav,
  type SectionSideNavItem,
} from "~/components/section_side_nav";
import { useCanExecuteOperation } from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";
import { clientRedirectUriSchema } from "~/entities/create_client";

import { AccountStatusBanner } from "./AccountStatusBanner";
import { ApiKeyEditor } from "./ApiKeyEditor";
import { CustomizationEditor } from "./CustomizationEditor";
import { EncodedClientKeyDialog } from "./EncodedClientKeyDialog";
import { F2FTimelines } from "./F2FTimelines";
import { OnlineTimelines } from "./OnlineTimelines";
import {
  RedirectURIsEditorWithIds,
  type RedirectUriWithId,
} from "./RedirectURIsEditor";
import { RolesAndPermissions } from "./RolesAndPermissions";
import { WebhooksEditor } from "./WebhooksEditor";

export function Client() {
  const canExecuteOperation = useCanExecuteOperation();
  const { localization } = useConfig();
  // RedirectURIs are updated via the updateClient operation.
  const canUpdateRedirectURIs = canExecuteOperation(
    operationIds["core.updateClient"],
  );

  const canVerifyOboAccount = canExecuteOperation(
    operationIds["core.getClientOnboardingSession"],
  );

  const clientId = useParams({
    from: "/clients/$clientId",
    select(params) {
      return params.clientId;
    },
  });

  const { data: clientData, isFetching: isClientFetching } =
    Query.useCore_getClientQuery(clientId);

  const { data: clientSecretData } = Query.useCore_getClientSecretQuery(
    clientId,
    {
      enabled: canExecuteOperation(operationIds["core.getClientSecret"]),
    },
  );

  const { data: apiKeyData, isFetching: isApiKeyFetching } =
    Query.useCore_getClientApiKeyQuery(clientId, {
      enabled: canExecuteOperation(operationIds["core.getClientApiKey"]),
    });

  const { data: webhooksData, isFetching: isWebhooksFetching } =
    Query.useCore_getClientWebhooksQuery(clientId, {
      enabled: canExecuteOperation(operationIds["core.getClientWebhooks"]),
    });

  const { data: customizationData, isFetching: isCustomizationFetching } =
    Query.useCore_getClientCustomizationQuery(clientId, {
      enabled: canExecuteOperation(operationIds["core.getClientCustomization"]),
    });

  const [isEncodedDialogOpened, setEncodedDialog] = useState(false);
  const [editedWebhooks, setEditedWebhooks] = useState<IClientWebhook[]>([]);
  const [redirectUris, setRedirectUris] = useState<RedirectUriWithId[]>([]);

  const queryClient = useQueryClient();

  const { mutate: createWebhook, isPending: isCreatingWebhook } =
    Query.useCore_createClientWebhookMutation(clientId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientWebhooksQueryKey(clientId),
        });
      },
      meta: {
        success: "Webhook created",
      },
    });

  const { mutate: updateWebhook, isPending: isUpdatingWebhook } =
    Query.useCore_updateClientWebhookMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientWebhooksQueryKey(clientId),
        });
      },
      meta: {
        success: "Webhook updated",
      },
    });

  const { mutate: deleteWebhook, isPending: isDeletingWebhook } =
    Query.useCore_deleteClientWebhookMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientWebhooksQueryKey(clientId),
        });
      },
      meta: {
        success: "Webhook deleted",
      },
    });

  const { mutate: updateClient, isPending: isUpdatingClient } =
    Query.useCore_updateClientMutation(clientId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientQueryKey(clientId),
        });
      },
      meta: {
        success: "Client updated",
      },
    });

  useEffect(() => {
    if (clientData?.name) {
      document.title = `Client Settings: ${clientData.name}`;
    }
  }, [clientData?.name]);

  useEffect(() => {
    if (!clientData?.redirect_uris) {
      return;
    }

    setRedirectUris((current) => {
      if (current.length === clientData.redirect_uris.length) {
        return current.map((item, index) => ({
          ...item,
          uri: clientData.redirect_uris[index],
        }));
      }

      return clientData.redirect_uris.map((uri: string, index: number) => ({
        id: current[index]?.id ?? crypto.randomUUID(),
        uri,
      }));
    });
  }, [clientData?.redirect_uris]);

  const handleRedirectUriSave = useCallback(
    (row: RedirectUriWithId) => {
      const existingIndex = redirectUris.findIndex((r) => r.id === row.id);
      let updatedUris: RedirectUriWithId[];

      if (existingIndex >= 0) {
        updatedUris = redirectUris.map((r) => (r.id === row.id ? row : r));
      } else {
        updatedUris = [...redirectUris, row];
      }

      const uriStrings = updatedUris
        .map((r) => r.uri.trim())
        .filter((uri) => uri !== "");

      updateClient(UpdateClientBody.fromJS({ redirect_uris: uriStrings }));
    },
    [redirectUris, updateClient],
  );

  const handleRedirectUriDelete = useCallback(
    (row: RedirectUriWithId) => {
      const updatedUris = redirectUris.filter((r) => r.id !== row.id);

      const uriStrings = updatedUris
        .map((r) => r.uri.trim())
        .filter((uri) => uri !== "");

      updateClient(UpdateClientBody.fromJS({ redirect_uris: uriStrings }));
    },
    [redirectUris, updateClient],
  );

  const handleWebhooksOnChange = useCallback((webhooks: IClientWebhook[]) => {
    setEditedWebhooks(webhooks);
  }, []);

  useEffect(() => {
    setEditedWebhooks(webhooksData ?? []);
  }, [webhooksData]);

  const handleWebhookSave = useCallback(
    (webhook: IClientWebhook) => {
      const original = webhooksData?.find(
        (w: IClientWebhook) => w.id === webhook.id,
      );

      if (!original) {
        createWebhook(
          CreateClientWebhookBody.fromJS({
            url: webhook.url,
            username: webhook.username,
            password: webhook.password,
          }),
        );
      } else {
        if (
          webhook.url !== original.url ||
          webhook.username !== original.username ||
          webhook.password !== original.password
        ) {
          updateWebhook({
            client_id: clientId,
            webhook_id: webhook.id,
            updateClientWebhookBody: UpdateClientWebhookBody.fromJS({
              url: webhook.url,
              username: webhook.username,
              password: webhook.password,
            }),
          });
        }
      }
    },
    [createWebhook, updateWebhook, clientId, webhooksData],
  );

  const handleWebhookDelete = useCallback(
    (webhook: IClientWebhook) => {
      const original = webhooksData?.find(
        (w: IClientWebhook) => w.id === webhook.id,
      );
      if (original) {
        deleteWebhook({ client_id: clientId, webhook_id: webhook.id });
      }
    },
    [deleteWebhook, webhooksData, clientId],
  );

  const isRedirectUrisLoading = isClientFetching || isUpdatingClient;

  const isWebhooksLoading =
    isWebhooksFetching ||
    isUpdatingWebhook ||
    isDeletingWebhook ||
    isCreatingWebhook;

  const canCreateWebhook = canExecuteOperation(
    operationIds["core.createClientWebhook"],
  );
  const canDeleteWebhook = canExecuteOperation(
    operationIds["core.deleteClientWebhook"],
  );
  const canUpdateWebhook = canExecuteOperation(
    operationIds["core.updateClientWebhook"],
  );

  const shouldShowClientTimelines =
    canExecuteOperation(operationIds["core.getClientTimelines"]) ||
    canExecuteOperation(operationIds["core.getClientF2fTimelines"]);

  const hasPluginKey = canExecuteOperation(
    operationIds["core.getClientApiKey"],
  );
  const hasBranding = canExecuteOperation(
    operationIds["core.getClientCustomization"],
  );

  const sideNavItems = useMemo<SectionSideNavItem[]>(() => {
    const items: SectionSideNavItem[] = [];
    if (canVerifyOboAccount) {
      items.push({ id: "account-status", label: "Account Status" });
    }

    items.push(
      { id: "general", label: "Client Settings" },
      { id: "redirects", label: "Redirect URIs" },
      { id: "webhooks", label: "Webhooks" },
      { id: "roles", label: "Roles & Permissions" },
    );
    if (hasPluginKey) {
      items.push({ id: "plugin-key", label: "Plugin Key" });
      items.push({ id: "api-key", label: "API Key" });
    }
    if (hasBranding) {
      items.push({ id: "branding", label: "Actions Page Customization" });
    }
    if (shouldShowClientTimelines) {
      items.push({ id: "timelines", label: "Custom Timelines" });
    }
    return items;
  }, [
    hasPluginKey,
    shouldShowClientTimelines,
    hasBranding,
    canVerifyOboAccount,
  ]);

  const theme = useTheme();

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: { md: 4.5 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav items={sideNavItems} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={{ xs: 5, lg: 10 }}>
          <Stack spacing={{ xs: 3, lg: 5 }}>
            <Box id="account-status">
              <AccountStatusBanner
                clientId={clientId}
                connectedAccountId={
                  clientData?.on_behalf_of_stripe_connected_account_id
                }
              />
            </Box>
            <Stack
              id="general"
              spacing={{ xs: 3, lg: 5 }}
              sx={{
                backgroundColor: "primary._states.hover",
                p: { xs: 2, sm: 3, lg: 5 },
                borderRadius: 2,
                scrollMarginTop: theme.spacing(10),
              }}
            >
              <Stack spacing={1}>
                <HeadingContainer
                  title={
                    <>
                      <PublicSvgIcon name="code_blocks" /> Client Settings
                      <Tooltip
                        title={`Created: ${clientData?.created?.toLocaleString(localization.locale, { timeZone: localization.timeZone })}
                    Updated: ${clientData?.updated?.toLocaleString(localization.locale, { timeZone: localization.timeZone })}`}
                      >
                        <PublicSvgIcon name="info" />
                      </Tooltip>
                    </>
                  }
                  variant="h5"
                  borderType="solid"
                />
                {canExecuteOperation(operationIds["core.getClientSecret"]) && (
                  <HideableText
                    value={clientSecretData?.client_secret ?? ""}
                    label="Client Secret"
                    dataTestId="client-secret-text"
                  />
                )}
              </Stack>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                justifyContent="space-between"
                spacing={{ xs: 2, lg: 5 }}
              >
                <CopyableTextInput
                  value={clientData?.id ?? ""}
                  label="Client ID"
                  sx={{ flexGrow: 1, width: { xs: "100%", lg: "auto" } }}
                  dataTestId="client-id-text"
                />
                <CopyableTextInput
                  value={clientData?.name ?? ""}
                  label="Client Name"
                  sx={{ flexGrow: 1, width: { xs: "100%", lg: "auto" } }}
                  dataTestId="client-name-text"
                />
                <CopyableTextInput
                  value={clientData?.contact_email ?? ""}
                  label="Contact Email"
                  sx={{ flexGrow: 1, width: { xs: "100%", lg: "auto" } }}
                  dataTestId="client-contact_email-text"
                />
                <CopyableTextInput
                  value={clientData?.base_url ?? ""}
                  label="Base URL"
                  sx={{ flexGrow: 1, width: { xs: "100%", lg: "auto" } }}
                  dataTestId="client-base_url-text"
                />
              </Stack>
            </Stack>
            <Button
              component={Link}
              to={`/clients/${clientId}/fees`}
              variant="contained"
              data-testid="fee-structure-link"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                alignSelf: "flex-end",
              }}
              size="large"
            >
              Fee Structure
            </Button>
            {canExecuteOperation(operationIds["core.getClient"]) && (
              <Box id="redirects" sx={{ scrollMarginTop: theme.spacing(10) }}>
                <RedirectURIsEditorWithIds
                  value={redirectUris}
                  onChange={setRedirectUris}
                  loading={isRedirectUrisLoading}
                  canDelete={canUpdateRedirectURIs}
                  canEdit={canUpdateRedirectURIs}
                  canAdd={canUpdateRedirectURIs}
                  zodSchema={clientRedirectUriSchema}
                  redirectUriTooltipMessage="All Redirect URIs must share the same domain as your Base URL for security purposes. To authorize a different domain, please contact your Onboarding Specialist."
                  onSave={handleRedirectUriSave}
                  onDelete={handleRedirectUriDelete}
                />
              </Box>
            )}
          </Stack>

          {canExecuteOperation(operationIds["core.getClientWebhooks"]) && (
            <Box id="webhooks" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <WebhooksEditor
                onChange={handleWebhooksOnChange}
                value={editedWebhooks}
                isLoading={isWebhooksLoading}
                canAdd={canCreateWebhook}
                canEdit={canUpdateWebhook}
                canDelete={canDeleteWebhook}
                onSave={handleWebhookSave}
                onDelete={handleWebhookDelete}
              />
            </Box>
          )}

          {canExecuteOperation(operationIds["core.getClientUsers"]) && (
            <Box id="roles" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <RolesAndPermissions
                clientId={clientId}
                clientName={clientData?.name}
              />
            </Box>
          )}

          <Stack spacing={{ xs: 3, lg: 5 }}>
            {canExecuteOperation(operationIds["core.getClientApiKey"]) && (
              <Grid
                container
                spacing={{ xs: 3, lg: 6 }}
                sx={{
                  backgroundColor: "primary._states.hover",
                  borderRadius: 2,
                  height: "max-content",
                  p: { xs: 2, lg: 0 },
                }}
              >
                <Grid
                  sx={{
                    pl: { xs: 0, lg: 6 },
                    pt: { xs: 0, lg: 6 },
                    py: { xs: 0, lg: 6 },
                    scrollMarginTop: theme.spacing(10),
                  }}
                  size={{ xs: 12, lg: 6 }}
                  id="plugin-key"
                >
                  <Stack spacing={{ xs: 3, lg: 5 }}>
                    <Typography variant="h5SolidDivider">
                      <PublicSvgIcon name="vpn_key_alert" /> Plugin Key&nbsp;
                      <Tooltip title="This key is relevant only to WooCommerce or Shopify sellers who use the Trustap Payments plugin. Please input it in the plugin settings.">
                        <PublicSvgIcon name="info" />
                      </Tooltip>
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setEncodedDialog(true)}
                      fullWidth
                      sx={{ width: { lg: "auto" } }}
                      data-testid="view-plugin-key-button"
                    >
                      View Plugin Key
                    </Button>
                  </Stack>
                </Grid>
                <Grid
                  sx={{
                    pr: { xs: 0, lg: 6 },
                    pt: { xs: 0, lg: 6 },
                    py: { xs: 0, lg: 6 },
                    scrollMarginTop: theme.spacing(10),
                  }}
                  size={{ xs: 12, lg: 6 }}
                  id="api-key"
                >
                  <ApiKeyEditor
                    apiKey={apiKeyData?.api_key}
                    isLoading={isApiKeyFetching}
                    canEdit={false}
                  />
                </Grid>
              </Grid>
            )}
            {(canExecuteOperation(
              operationIds["core.getClientCustomization"],
            ) ||
              shouldShowClientTimelines) && (
              <Grid
                container
                spacing={{ xs: 3, lg: 6 }}
                sx={{
                  backgroundColor: "primary._states.hover",
                  borderRadius: 2,
                  height: "max-content",
                  p: { xs: 2, lg: 0 },
                }}
              >
                {canExecuteOperation(
                  operationIds["core.getClientCustomization"],
                ) && (
                  <Grid
                    sx={{
                      pl: { xs: 0, lg: 6 },
                      py: { xs: 0, lg: 6 },
                      scrollMarginTop: theme.spacing(10),
                    }}
                    size={{ xs: 12, lg: 6 }}
                    id="branding"
                  >
                    <CustomizationEditor
                      customizationData={customizationData}
                      isLoading={isCustomizationFetching}
                      canEdit={false}
                    />
                  </Grid>
                )}
                {shouldShowClientTimelines && (
                  <Grid
                    sx={{
                      pr: { xs: 0, lg: 6 },
                      py: { xs: 0, lg: 6 },
                      scrollMarginTop: theme.spacing(10),
                    }}
                    size={{ xs: 12, lg: 6 }}
                    id="timelines"
                  >
                    <Stack spacing={{ xs: 3, lg: 5 }}>
                      <Typography variant="h5SolidDivider">
                        <PublicSvgIcon name="history_2" /> Custom Timelines
                      </Typography>
                      {canExecuteOperation(
                        operationIds["core.getClientTimelines"],
                      ) && <OnlineTimelines clientId={clientId} mode="view" />}
                      {canExecuteOperation(
                        operationIds["core.getClientF2fTimelines"],
                      ) && <F2FTimelines clientId={clientId} mode="view" />}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            )}
          </Stack>

          {canExecuteOperation(operationIds["core.getClientApiKey"]) && (
            <EncodedClientKeyDialog
              open={isEncodedDialogOpened}
              onClose={() => setEncodedDialog(false)}
              webhooks={webhooksData ?? []}
              apiKey={apiKeyData?.api_key}
              clientSecret={clientSecretData?.client_secret}
              clientId={clientId}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
}
